import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

const GOOGLE_ELEMENT_ID = "google_translate_element";

const LANGUAGE_OPTIONS = [
  { code: "en", label: "English", flag: "🇺🇸", googleCode: "en" },
  { code: "he", label: "עברית", englishLabel: "Hebrew", flag: "🇮🇱", googleCode: "iw" },
  { code: "hi", label: "हिन्दी", englishLabel: "Hindi", flag: "🇮🇳", googleCode: "hi" },
  { code: "sw", label: "Kiswahili", englishLabel: "Swahili", flag: "🇰🇪", googleCode: "sw" },
  { code: "ak", label: "Akan (Twi)", flag: "🇬🇭", googleCode: "ak" },
  { code: "af", label: "Afrikaans", flag: "🇿🇦", googleCode: "af" },
  { code: "de", label: "Deutsch", englishLabel: "German", flag: "🇩🇪", googleCode: "de" },
  { code: "it", label: "Italiano", englishLabel: "Italian", flag: "🇮🇹", googleCode: "it" },
  { code: "es", label: "Español", englishLabel: "Spanish", flag: "🇪🇸", googleCode: "es" },
];

const MAX_TRANSLATION_RETRIES = 60;

const LanguageContext = createContext({
  language: "en",
  setLanguage: () => {},
  supportedLanguages: LANGUAGE_OPTIONS,
  isDetecting: true,
});

function getLanguageMetadata(code) {
  return LANGUAGE_OPTIONS.find((lang) => lang.code === code) ?? LANGUAGE_OPTIONS[0];
}

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState("en");
  const [isDetecting, setIsDetecting] = useState(true);
  const [translatorEnabled, setTranslatorEnabled] = useState(false);
  const isTranslatorReady = useRef(false);
  const queuedLanguageRef = useRef("en");
  const scriptInjectedRef = useRef(false);
  const translatorObserverRef = useRef(null);
  const translatorRetryIntervalRef = useRef(null);
  const translatorRetryAttemptsRef = useRef(0);

  const applyLanguageToDom = useCallback((code) => {
    const metadata = getLanguageMetadata(code);
    document.documentElement.lang = metadata?.code ?? "en";
  }, []);

  const resetGoogleTranslateCookie = useCallback(() => {
    if (typeof document === "undefined") return;
    const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `googtrans=/en/en; expires=${expires}; path=/`;
    document.cookie = `googtrans=/auto/en; expires=${expires}; path=/`;

    if (typeof window !== "undefined") {
      const hostname = window.location.hostname;
      if (hostname && hostname.includes(".") && hostname !== "localhost") {
        document.cookie = `googtrans=/en/en; expires=${expires}; path=/; domain=.${hostname}`;
        document.cookie = `googtrans=/auto/en; expires=${expires}; path=/; domain=.${hostname}`;
      }
    }
  }, []);

  const dispatchComboChange = useCallback((combo) => {
    if (!combo) return;
    try {
      const event = new Event("change", { bubbles: true });
      combo.dispatchEvent(event);
    } catch {
      const legacyEvent = document.createEvent("HTMLEvents");
      legacyEvent.initEvent("change", true, true);
      combo.dispatchEvent(legacyEvent);
    }
  }, []);

  const tryApplyGoogleTranslation = useCallback(
    (code) => {
      const combo = document.querySelector("select.goog-te-combo");
      const metadata = getLanguageMetadata(code);

      if (!combo || !metadata) {
        return false;
      }

      if (combo.value === metadata.googleCode) {
        return true;
      }

      combo.value = metadata.googleCode;
      dispatchComboChange(combo);
      return true;
    },
    [dispatchComboChange],
  );

  const syncGoogleTranslate = useCallback(
    (code) => {
      queuedLanguageRef.current = code;
      const applied = tryApplyGoogleTranslation(code);
      if (applied) {
        return;
      }

      if (translatorRetryIntervalRef.current || typeof window === "undefined") {
        return;
      }

      translatorRetryAttemptsRef.current = 0;
      translatorRetryIntervalRef.current = window.setInterval(() => {
        const wasApplied = tryApplyGoogleTranslation(queuedLanguageRef.current);
        if (wasApplied && translatorRetryIntervalRef.current) {
          translatorRetryAttemptsRef.current = 0;
          window.clearInterval(translatorRetryIntervalRef.current);
          translatorRetryIntervalRef.current = null;
          return;
        }

        translatorRetryAttemptsRef.current += 1;
        if (translatorRetryAttemptsRef.current >= MAX_TRANSLATION_RETRIES && translatorRetryIntervalRef.current) {
          window.clearInterval(translatorRetryIntervalRef.current);
          translatorRetryIntervalRef.current = null;
          translatorRetryAttemptsRef.current = 0;
        }
      }, 500);
    },
    [tryApplyGoogleTranslation],
  );

  const updateLanguageState = useCallback(
    (code, { syncTranslator = true } = {}) => {
      const metadata = getLanguageMetadata(code);
      if (!metadata) return;

      queuedLanguageRef.current = metadata.code;
      setLanguageState(metadata.code);
      applyLanguageToDom(metadata.code);

      if (syncTranslator) {
        if (isTranslatorReady.current) {
          syncGoogleTranslate(metadata.code);
        } else {
          queuedLanguageRef.current = metadata.code;
        }
      }
    },
    [applyLanguageToDom, syncGoogleTranslate],
  );

  const ensureTranslatorEnabled = useCallback(
    (code) => {
      if (code === "en" || translatorEnabled) return;
      setTranslatorEnabled(true);
    },
    [translatorEnabled],
  );

  const changeLanguage = useCallback(
    (code, options = {}) => {
      ensureTranslatorEnabled(code);
      updateLanguageState(code, { ...options, syncTranslator: translatorEnabled });
    },
    [ensureTranslatorEnabled, translatorEnabled, updateLanguageState],
  );

  const userSetLanguage = useCallback(
    (code) => {
      ensureTranslatorEnabled(code);
      updateLanguageState(code, { syncTranslator: translatorEnabled });
    },
    [ensureTranslatorEnabled, translatorEnabled, updateLanguageState],
  );

  const loadGoogleTranslate = useCallback(() => {
    if (scriptInjectedRef.current || typeof window === "undefined") return;
    scriptInjectedRef.current = true;
    resetGoogleTranslateCookie();

    window.googleTranslateElementInit = () => {
      if (!window.google?.translate) return;
      resetGoogleTranslateCookie();

      new window.google.translate.TranslateElement(
        {
          pageLanguage: "en",
          includedLanguages: LANGUAGE_OPTIONS.map((lang) => lang.googleCode).join(","),
          autoDisplay: false,
        },
        GOOGLE_ELEMENT_ID,
      );

      isTranslatorReady.current = true;
      syncGoogleTranslate(queuedLanguageRef.current);
    };

    const script = document.createElement("script");
    script.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    script.async = true;
    script.onerror = () => {
      isTranslatorReady.current = false;
    };
    document.body.appendChild(script);
  }, [resetGoogleTranslateCookie, syncGoogleTranslate]);

  useEffect(() => {
    if (!translatorEnabled) return;
    loadGoogleTranslate();
  }, [loadGoogleTranslate, translatorEnabled]);

  useEffect(() => {
    resetGoogleTranslateCookie();
  }, [resetGoogleTranslateCookie]);

  useEffect(() => {
    if (typeof MutationObserver === "undefined" || typeof document === "undefined") return undefined;
    if (translatorObserverRef.current) return undefined;

    const observer = new MutationObserver(() => {
      const applied = tryApplyGoogleTranslation(queuedLanguageRef.current);
      if (applied && translatorObserverRef.current) {
        translatorObserverRef.current.disconnect();
        translatorObserverRef.current = null;
      }
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
    translatorObserverRef.current = observer;

    return () => {
      observer.disconnect();
      translatorObserverRef.current = null;
    };
  }, [tryApplyGoogleTranslation]);

  useEffect(
    () => () => {
      if (translatorRetryIntervalRef.current) {
        window.clearInterval(translatorRetryIntervalRef.current);
        translatorRetryIntervalRef.current = null;
      }
      if (translatorObserverRef.current) {
        translatorObserverRef.current.disconnect();
        translatorObserverRef.current = null;
      }
    },
    [],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    updateLanguageState("en", { syncTranslator: false });
    setIsDetecting(false);
  }, [updateLanguageState]);

  const contextValue = useMemo(
    () => ({
      language,
      setLanguage: userSetLanguage,
      supportedLanguages: LANGUAGE_OPTIONS,
      isDetecting,
    }),
    [isDetecting, language, userSetLanguage],
  );

  return (
    <LanguageContext.Provider value={contextValue}>
      <div id={GOOGLE_ELEMENT_ID} aria-hidden="true" />
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}

